import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import Cookies from "js-cookie";
import {
  VisibilityIcon,
  VisibilityOffIcon,
  WarningIcon
} from "@echo-health/icons-web";
import styled from "../../styled";
import { isClient } from "../../global";
import { validateEmail } from "../../utils/validators";
import { useAuthenticateMutation } from "../../graphql/hooks/useAuthenticateMutation";
import { dropExistingUserCookie } from "../../marketing";
import { DispatchProp, State } from "../../store/types";
import { decode, encode } from "../../utils/queryString";
import AnonymousOnly from "../../components/AnonymousOnly";
import { ButtonGroup } from "../../components/DeprecatedButton";
import Form from "../../components/Form";
import { Heading } from "../../design-system/components/Heading";
import Icon from "../../components/Icon";
import { Stack } from "../../components/Stack";
import { Text } from "../../design-system/components/Text";
import { DisplayHeading } from "../../design-system/components/DisplayHeading";
import { TextInput } from "../../design-system/components/TextInput";
import { TextLink } from "../../design-system/components/TextLink";
import { Button } from "../../design-system/components/Button";
import { getNHSLoginAuthInfo, NhsLoginInfo } from "../../utils/nhsLogin";
import { authenticateWithIntercom } from "../../login";
import { StateProps } from "./Root";
import { DividerWithLabel } from "../../design-system/components/DividerWithLabel";
import { NHSLoginButton } from "../../components/NHSLoginButton";
import Divider from "../../components/Divider";
import { rebootIntercom } from "../../intercom";
import { StandaloneLayout } from "../../components/StandaloneLayout";
import { Card } from "../../design-system/components/Card";
import { LinkButton } from "../../design-system/components/LinkButton";
import useErrorModal, { getErrorVariant } from "../../components/ErrorModal";
import { Modal } from "../../design-system/components/Modal";
import {
  deprecatedTrack,
  reset as analyticsReset,
  trackEvent
} from "../../analytics";

const ForgotPasswordWrapper = styled.div`
  padding: ${p => `${p.theme.spacing.s} 12px 0`};
`;

type Props = RouteComponentProps & DispatchProp & StateProps;

const Login: React.FC<Props> = props => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginError, setIsLoginError] = useState(false);
  const [isNoPatientError, setIsNoPatientError] = useState(false);
  const [nhsLoginInfo, setNHSLoginInfo] = useState<NhsLoginInfo | null>(null);
  const { handleError } = useErrorModal();

  const [authenticate, { loading }] = useAuthenticateMutation({
    onError: error => {
      deprecatedTrack("login_failure");
      handleError({ variant: getErrorVariant(error) });
    },
    onCompleted: response => {
      const hasAuthErrors = response.authenticate.errors.length > 0;
      if (hasAuthErrors) {
        deprecatedTrack("login_failure_not_valid");
        setIsLoginError(true);
        return;
      }

      if (response.authenticate.account !== null) {
        const account = response.authenticate.account;
        const accountId = account.id;
        authenticateWithIntercom({
          accountId,
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email,
          intercomId: account.intercomId,
          dispatch: props.dispatch,
          intercomUserHash: account.intercomUserHash
        });
      }

      dropExistingUserCookie();
      deprecatedTrack("login_successful");
      const query = decode(props.location.search);
      props.history.replace(query.returnUrl ? query.returnUrl[0] : "/");
    }
  });

  useEffect(() => {
    const query = decode(props.location.search);

    if (query.sessionInvalid && query.sessionInvalid[0] === "true") {
      analyticsReset();
      rebootIntercom();
      const query = decode(props.location.search);
      delete query.sessionInvalid;
      const newQuery = encode(query);
      props.history.replace(props.location.pathname + newQuery);
      return;
    }

    // Don't use usePageView hook as we want to run this on the same render as if we reset analytics
    trackEvent("login_pageview");
  }, [props.location.search, props.location.pathname, props.history]);

  useEffect(() => {
    const nhsLoginInfo = getNHSLoginAuthInfo();
    setNHSLoginInfo(nhsLoginInfo);
    if (isClient()) {
      Cookies.set("nhs_login_auth_state", nhsLoginInfo.state, {
        domain: ".echo.co.uk",
        secure: true
      });
      Cookies.set("nhs_login_nonce", nhsLoginInfo.nonce, {
        domain: ".echo.co.uk",
        secure: true
      });
    }
  }, []);

  const isNhsLoginEnabled =
    props.flags &&
    props.flags.length > 0 &&
    props.flags.find(flag => flag.name === "nhs_login_login" && flag.isEnabled);

  return (
    <AnonymousOnly>
      <Helmet>
        <title>Log in | Echo</title>
      </Helmet>

      <StandaloneLayout contentWidth="420">
        <Card margin="0 0 m">
          <DisplayHeading margin="0 0 l">Log in</DisplayHeading>
          <Form
            onSubmit={() => {
              deprecatedTrack("login_clicked");
              const validatedEmail = validateEmail(email);

              if (validatedEmail) {
                setEmailError(validatedEmail);
                return;
              }

              if (password.trim() === "") {
                setPasswordError("Please enter a password");
                return;
              }

              authenticate({
                variables: {
                  email: email.trim(),
                  password
                }
              });
            }}
          >
            <Stack
              vertical={true}
              spaceBetween={"l"}
              alignment={"stretch"}
              margin="0 0 l 0"
            >
              <TextInput
                type="email"
                label="Email address"
                id="EmailId"
                name="email"
                value={email}
                onChange={e => {
                  setEmail(e.currentTarget.value);
                  setEmailError("");
                }}
                onBlur={() => {
                  setEmailError(validateEmail(email));
                }}
                errorMessage={emailError}
              />
              <div>
                <TextInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  id="passwordId"
                  name="password"
                  value={password}
                  onChange={e => {
                    setPassword(e.currentTarget.value);
                    setPasswordError("");
                  }}
                  errorMessage={passwordError}
                  iconTrailing={{
                    icon: showPassword ? VisibilityIcon : VisibilityOffIcon,
                    type: "button",
                    onClick: () => {
                      setShowPassword(!showPassword);
                    },
                    "aria-label": showPassword
                      ? "Hide password"
                      : "Show password"
                  }}
                />
                <ForgotPasswordWrapper>
                  <TextLink
                    label="Forgot your password?"
                    linkTo="/forgot-password"
                    type="internal"
                    size="s"
                  />
                </ForgotPasswordWrapper>
              </div>
            </Stack>

            <Button
              label="Log in"
              isLoading={loading}
              shouldSubmit={true}
              onClick={() => trackEvent("login", { auth_type: "email" })}
            />
          </Form>

          {isNhsLoginEnabled && (
            <>
              <DividerWithLabel color="gray70" size="s" margin="m 0">
                OR
              </DividerWithLabel>
              <NHSLoginButton
                type="login"
                href={nhsLoginInfo ? nhsLoginInfo.authUrl : ""}
              />
            </>
          )}
        </Card>

        <Card>
          <Text isBold margin="0 0 s">
            Don’t have an account?
          </Text>
          <Text margin="0 0 m">
            Join the 400,000 people who already trust us with their NHS
            prescriptions
          </Text>
          <LinkButton
            label="Sign up for free"
            linkTo="/v2/sign-up"
            variant="secondary"
          />
        </Card>
      </StandaloneLayout>

      <Modal
        title="Incorrect email or password"
        isTitleVisible={false}
        isOpen={isLoginError}
        onClose={() => {
          setIsLoginError(false);
        }}
      >
        <Icon margin="0 0 m" size="l" color="orange40" type={WarningIcon} />
        <Heading margin="0 0 s">Incorrect email or password</Heading>
        <Text>Unfortunately those login details aren't right.</Text>
        <Divider margin="l 0" />
        <ButtonGroup reverse>
          <Button
            label="Try again"
            onClick={() => {
              setIsLoginError(false);
              deprecatedTrack("incorrect_login_try_again");
            }}
          />
        </ButtonGroup>
      </Modal>

      <Modal
        title="Something's not right"
        isTitleVisible={false}
        isOpen={isNoPatientError}
        onClose={() => {
          setIsNoPatientError(false);
        }}
      >
        <Icon margin="0 0 m" size="l" color="orange40" type={WarningIcon} />
        <Heading margin="0 0 s">Something's not right</Heading>
        <Text>
          It appears that you haven’t completed the sign up process. Before you
          can start using Echo you'll need to complete this.
        </Text>
        <Divider margin="l 0" />
        <ButtonGroup reverse>
          <LinkButton
            label="Complete your account"
            linkTo="/v2/sign-up"
            onClick={() => {
              setIsNoPatientError(false);
            }}
          />
        </ButtonGroup>
      </Modal>
    </AnonymousOnly>
  );
};

function mapStateToProps(state: State): StateProps {
  return {
    authenticated: state.auth.authenticated,
    flags: state.features.flags
  };
}

export default connect(mapStateToProps)(withRouter(Login));
